import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, MessageCircle, Share2, Send, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GalleryCard = ({ item, index = 0 }) => {
  const [likes, setLikes] = useState(item.likes);
  const [liked, setLiked] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [shared, setShared] = useState(false);

  const like = async () => {
    if (liked) return;
    setLiked(true);
    setLikes((n) => n + 1);
    try {
      const { data } = await axios.post(`${API}/gallery/${item.id}/like`);
      setLikes(data.likes);
    } catch {
      setLiked(false);
      setLikes((n) => n - 1);
    }
  };

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) {
      try {
        const { data } = await axios.get(`${API}/gallery/${item.id}/comments`);
        setComments(data);
      } catch {
        setComments([]);
      }
    }
  };

  const postComment = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return;
    setPosting(true);
    try {
      const { data } = await axios.post(`${API}/gallery/${item.id}/comments`, {
        name: name.trim(),
        text: text.trim(),
      });
      setComments((c) => [...(c || []), data]);
      setText("");
      toast.success("Comment posted");
    } catch {
      toast.error("Could not post comment");
    } finally {
      setPosting(false);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/gallery/${item.id}`;
    const payload = { title: item.title, text: item.caption, url };
    try {
      if (navigator.share) {
        await navigator.share(payload);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
      setShared(true);
      axios.post(`${API}/gallery/${item.id}/share`).catch(() => {});
      setTimeout(() => setShared(false), 2000);
    } catch {
      /* share cancelled */
    }
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="group rounded-3xl bg-white border border-coal/5 overflow-hidden shadow-[0_4px_24px_-12px_rgba(26,29,36,0.15)] hover:shadow-[0_20px_50px_-20px_rgba(13,124,133,0.35)] transition-shadow duration-500"
      data-testid={`gallery-card-${item.id}`}
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={item.image}
          alt={item.title}
          loading="lazy"
          className={`h-full w-full transition-transform duration-700 group-hover:scale-105 ${
            item.fit === "contain" ? "bg-[#EDE6DA] object-contain" : "object-cover"
          }`}
        />
        <span className="absolute left-4 top-4 rounded-full bg-coal-dark/80 backdrop-blur px-3 py-1 font-mono-brand text-[11px] font-bold tracking-widest text-sand">
          {item.year}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-heading font-bold text-coal text-lg leading-snug">{item.title}</h3>
        <p className="text-sm text-coal-muted leading-relaxed mt-2">{item.caption}</p>
        <div className="mt-4 flex items-center gap-2 border-t border-coal/5 pt-4">
          <button
            data-testid={`like-button-${item.id}`}
            onClick={like}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-all duration-300 ${
              liked ? "bg-ember text-sand" : "bg-ember-subtle text-ember hover:bg-ember hover:text-sand"
            }`}
          >
            <Heart size={15} className={liked ? "fill-current" : ""} />
            <span data-testid={`like-count-${item.id}`}>{likes}</span>
          </button>
          <button
            data-testid={`comment-toggle-${item.id}`}
            onClick={toggleComments}
            className="flex items-center gap-1.5 rounded-full bg-teal-subtle px-3 py-1.5 text-sm font-semibold text-teal hover:bg-teal hover:text-sand transition-all duration-300"
          >
            <MessageCircle size={15} />
            {(comments ?? item.comments)?.length ?? 0}
          </button>
          <button
            data-testid={`share-button-${item.id}`}
            onClick={share}
            className="ml-auto flex items-center gap-1.5 rounded-full bg-coal/5 px-3 py-1.5 text-sm font-semibold text-coal hover:bg-coal hover:text-sand transition-all duration-300"
          >
            {shared ? <Check size={15} /> : <Share2 size={15} />}
            {shared ? "Shared" : "Share"}
          </button>
        </div>
        <AnimatePresence>
          {showComments && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid={`comments-panel-${item.id}`}
            >
              <div className="mt-4 space-y-3 max-h-44 overflow-y-auto pr-1">
                {comments === null ? (
                  <p className="text-xs text-coal-muted">Loading comments...</p>
                ) : comments.length === 0 ? (
                  <p className="text-xs text-coal-muted">Be the first to leave a comment.</p>
                ) : (
                  comments.map((c) => (
                    <div key={c.id} className="rounded-xl bg-sand px-3 py-2">
                      <span className="font-heading font-bold text-xs text-teal">{c.name}</span>
                      <p className="text-sm text-coal leading-snug">{c.text}</p>
                    </div>
                  ))
                )}
              </div>
              <form onSubmit={postComment} className="mt-3 space-y-2">
                <input
                  data-testid={`comment-name-${item.id}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  maxLength={60}
                  className="w-full rounded-xl border border-coal/10 bg-white px-3 py-2 text-sm outline-none focus:border-teal transition-colors"
                />
                <div className="flex gap-2">
                  <input
                    data-testid={`comment-input-${item.id}`}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Say something lekker..."
                    maxLength={500}
                    className="flex-1 rounded-xl border border-coal/10 bg-white px-3 py-2 text-sm outline-none focus:border-teal transition-colors"
                  />
                  <button
                    data-testid={`comment-submit-${item.id}`}
                    type="submit"
                    disabled={posting}
                    className="rounded-xl bg-teal px-3.5 text-sand hover:bg-teal-deep transition-colors disabled:opacity-50"
                    aria-label="Post comment"
                  >
                    <Send size={15} />
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
};
